/**
 * Detects whether a processing trigger is active for the current page.
 *
 * Two trigger sources:
 *   A. URL query param `?process-completed=true` — set by epics redirect after process completion
 *   B. Redux `subtitleGeneration` — set by CC modal after Generate click
 *
 * The trigger is "consumed" (one-shot): once detected, it remains active for
 * the lifetime of the component even if the URL param is removed.
 */

import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router';

export const useProcessingTrigger = (contentUid) => {
  const location = useLocation();
  const history = useHistory();
  const generation = useSelector((state) => state.subtitleGeneration);

  // Counter that increments on each trigger event (allows re-triggering)
  const [triggerCount, setTriggerCount] = useState(0);

  // Trigger A: ?process-completed=true in URL (from epics redirect)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('process-completed') === 'true') {
      setTriggerCount((c) => c + 1);
      params.delete('process-completed');
      const cleanSearch = params.toString();
      const cleanPath =
        location.pathname + (cleanSearch ? `?${cleanSearch}` : '');
      history.replace(cleanPath);
    }
  }, [location.search, location.pathname, history]);

  // Trigger B: subtitle generation started for this content
  const lastGenerationRef = useRef(null);
  useEffect(() => {
    if (
      generation?.uid &&
      generation.uid === contentUid &&
      generation.startedAt !== lastGenerationRef.current
    ) {
      lastGenerationRef.current = generation.startedAt;
      setTriggerCount((c) => c + 1);
    }
  }, [generation?.uid, generation?.startedAt, contentUid]);

  return triggerCount;
};

export default useProcessingTrigger;
